import React, { useMemo } from 'react';
import { MathText, containsMath } from './MathText';

interface QuestionRendererProps {
  text: string;
  className?: string;
}

/**
 * Unified question content renderer.
 *
 * Auto-detects content type and renders appropriately:
 * - Math: LaTeX via KaTeX ($...$ inline, $$...$$ display)
 * - Code: Syntax-highlighted code blocks (```...```)
 * - Plain text: Default rendering
 *
 * This component is extensible for future content types
 * like chemical formulas, diagrams, etc.
 */
export function QuestionRenderer({ text, className }: QuestionRendererProps) {
  const rendered = useMemo(() => {
    if (!text) return null;

    // Check for code blocks first (```...```)
    if (containsCodeBlock(text)) {
      return renderWithCodeBlocks(text);
    }

    // Check for math content ($...$ or $$...$$)
    const hasMath = containsMath(text);
    const hasDisplay = containsDisplayMath(text);

    if (hasMath || hasDisplay) {
      return <MathText text={text} className={className} />;
    }

    // Plain text
    return <span className={className}>{text}</span>;
  }, [text, className]);

  return <>{rendered}</>;
}

/**
 * Check if text contains display math ($$...$$)
 */
function containsDisplayMath(text: string): boolean {
  return /\$\$[^$]+\$\$/.test(text);
}

/**
 * Check if text contains code blocks
 */
function containsCodeBlock(text: string): boolean {
  return /```[\s\S]*?```/.test(text);
}

/**
 * Render text with code blocks
 * Handles mixed content: text, math, and code
 */
function renderWithCodeBlocks(text: string): React.ReactNode {
  // Split on code blocks, keeping delimiters
  const parts = text.split(/(```[\s\S]*?```)/g);

  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith('```') && part.endsWith('```')) {
          // Extract language and code
          const match = part.match(/```(\w*)\n?([\s\S]*?)```/);
          if (match) {
            const [, language, code] = match;
            return (
              <CodeBlock key={index} code={code.trim()} language={language} />
            );
          }
          return <code key={index}>{part.slice(3, -3)}</code>;
        }
        // Non-code part - could contain math
        if (containsMath(part)) {
          return <MathText key={index} text={part} />;
        }
        return <span key={index}>{part}</span>;
      })}
    </>
  );
}

/**
 * Simple code block renderer
 * Uses basic styling - can be enhanced with syntax highlighting library
 */
function CodeBlock({ code, language }: { code: string; language?: string }) {
  return (
    <pre
      style={{
        backgroundColor: 'var(--bg-secondary, #1e1e1e)',
        padding: '0.75rem 1rem',
        borderRadius: '0.375rem',
        overflow: 'auto',
        fontSize: '0.875rem',
        fontFamily: 'monospace',
        margin: '0.5rem 0',
      }}
    >
      {language && (
        <div
          style={{
            fontSize: '0.75rem',
            color: 'var(--text-muted, #888)',
            marginBottom: '0.5rem',
          }}
        >
          {language}
        </div>
      )}
      <code>{code}</code>
    </pre>
  );
}

/**
 * Content type detection utilities
 */
export const ContentDetection = {
  hasMath: containsMath,
  hasDisplayMath: containsDisplayMath,
  hasCodeBlock: containsCodeBlock,

  /**
   * Detect the primary content type of text
   */
  detectType(text: string): 'math' | 'code' | 'text' {
    if (containsCodeBlock(text)) return 'code';
    if (containsMath(text) || containsDisplayMath(text)) return 'math';
    return 'text';
  },
};
